import type { InputHTMLAttributes, ReactNode } from "react";
import "./Switch.css";

interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "children" | "role"> {
  /** Rendered beside the track. Omit when a visible label already names the control. */
  label?: ReactNode;
  /** Secondary line under the label, for explaining what the switch governs. */
  description?: ReactNode;
}

/**
 * An on/off switch.
 *
 * Use this rather than a {@link Checkbox} when the change takes effect immediately. A checkbox
 * implies a form the user will submit; a switch says the thing is now on. Staff access is the
 * reference case — each toggle saves on the spot.
 *
 * A real `<input type="checkbox">` carries the state and the keyboard behaviour, with
 * `role="switch"` so it is announced as on/off rather than checked/unchecked. The track and thumb
 * are decorative siblings.
 */
export const Switch = ({
  label,
  description,
  className = "",
  disabled,
  ...props
}: SwitchProps) => (
  <label
    className={`switch${disabled ? " switch--disabled" : ""} ${className}`.trim()}
  >
    <span className="switch__control">
      <input
        type="checkbox"
        role="switch"
        className="switch__input"
        disabled={disabled}
        {...props}
      />
      {/* aria-hidden: the input already carries the state, so this must not be announced too. */}
      <span className="switch__track" aria-hidden="true">
        <span className="switch__thumb" />
      </span>
    </span>
    {(label !== undefined || description !== undefined) && (
      <span className="switch__text">
        {label !== undefined && <span className="switch__label">{label}</span>}
        {description !== undefined && (
          <span className="switch__description">{description}</span>
        )}
      </span>
    )}
  </label>
);
